/* ============================================================
   Добор профиля после входа через Google/Telegram.

   Провайдер отдаёт только имя и (иногда) email — ни ника, ни
   телефона у нас нет, а витрине они нужны: ник — публичное имя
   игрока, телефон — связь по заявке. Ник из Google намеренно НЕ
   подставляем: человек выбирает его сам.

   Модалка показывается поверх любой страницы, пока
   user.needs_onboarding === true, и закрыть её можно только
   заполнив форму либо выйдя из аккаунта.
   ============================================================ */
import { useState } from 'react';
import { useAppStore } from '../store';
import Modal from './Modal';
import Btn from './Btn';
import Spinner from './Spinner';
import { Field, Input } from './Field';
import PhoneInput, { isValidKzPhone, rawPhone } from './PhoneInput';
import { containsProfanity } from '../utils/profanity';

const NICK_RE = /^[a-zA-Z0-9._-]{3,20}$/;

export default function OnboardingModal() {
  const user = useAppStore((s) => s.user);
  const [f, setF] = useState({ nick: '', phone: '', email: '' });
  const [err, setErr] = useState({});
  const [srvErr, setSrvErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => {
    setF((s) => ({ ...s, [k]: v }));
    setErr((e) => ({ ...e, [k]: null }));
    setSrvErr(null);
  };

  if (!user || !user.needs_onboarding) return null;

  // Email спрашиваем только у тех, у кого его нет (вход через Telegram)
  const askEmail = !user.email;

  const submit = async () => {
    const e = {};
    if (!NICK_RE.test(f.nick.trim())) e.nick = 'Никнейм: 3–20 символов, латиница, цифры, . _ -';
    else if (containsProfanity(f.nick.trim())) e.nick = 'Такой никнейм использовать нельзя';
    if (!isValidKzPhone(f.phone)) e.phone = 'Введите номер в формате +7 (7XX) XXX-XX-XX';
    if (askEmail && f.email.trim() && !/.+@.+\..+/.test(f.email.trim())) e.email = 'Некорректный e-mail';
    setErr(e);
    setSrvErr(null);
    if (Object.keys(e).length) return;

    setBusy(true);
    try {
      await useAppStore.getState().completeProfile({
        nickname: f.nick.trim(),
        phone: rawPhone(f.phone),
        email: askEmail ? f.email.trim() : undefined,
      });
    } catch (ex) {
      // Тост стора здесь бесполезен: модалка заперта поверх страницы и
      // перекрывает его, так что отказ сервера выглядел как «кнопка не
      // работает». Показываем причину внутри формы — по возможности прямо
      // у поля, из-за которого сервер вернул 400 (ник занят и т.п.).
      const msg = ex?.message || 'Не удалось сохранить профиль';
      const field = /н[иі]кнейм/i.test(msg) ? 'nick'
        : /телефон/i.test(msg) ? 'phone'
        : /e-?mail|почт/i.test(msg) ? 'email'
        : null;
      if (field) setErr((s) => ({ ...s, [field]: msg }));
      else setSrvErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const providerName = user.oauth_provider === 'telegram' ? 'Telegram' : 'Google';

  return (
    <Modal open locked width={480}>
      <div style={{ padding: 30 }}>
        <h2 className="display" style={{ fontSize: 25, margin: '0 0 6px' }}>Ещё пара шагов</h2>
        <p style={{ color: 'var(--fg-muted)', margin: '0 0 22px', fontSize: 14, lineHeight: 1.5 }}>
          Вход через {providerName} выполнен{user.full_name ? `, ${user.full_name}` : ''}. Осталось выбрать
          никнейм и оставить телефон — под этим ником вы будете в составах команд и в сетке турнира.
        </p>

        <div className="col gap-4">
          <Field label="Никнейм" required error={err.nick} hint="3–20 символов: латиница, цифры, точка, дефис, подчёркивание">
            <Input
              placeholder="s1lent"
              value={f.nick}
              error={err.nick}
              autoFocus
              onChange={(e) => set('nick', e.target.value)}
            />
          </Field>

          <Field label="Телефон" required error={err.phone} valid={isValidKzPhone(f.phone)}>
            <PhoneInput value={f.phone} error={err.phone} onChange={(v) => set('phone', v)} />
          </Field>

          {askEmail && (
            <Field label="E-mail" error={err.email} hint="Необязательно — Telegram его не передаёт">
              <Input
                type="email"
                placeholder="you@mail.kz"
                value={f.email}
                error={err.email}
                onChange={(e) => set('email', e.target.value)}
              />
            </Field>
          )}

          {srvErr && (
            <div style={{
              padding: '12px 14px', borderRadius: 'var(--r-md)',
              background: 'rgba(225,18,23,0.10)', border: '1px solid var(--danger)',
              color: 'var(--danger)', fontSize: 13, lineHeight: 1.45,
            }}>
              {srvErr}
            </div>
          )}

          <Btn size="lg" full onClick={submit} disabled={busy} icon={busy ? <Spinner size={18} /> : null}>
            {busy ? 'Сохраняем…' : 'Готово'}
          </Btn>

          <button
            onClick={() => useAppStore.getState().logout()}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, color: 'var(--fg-muted)', padding: 4,
            }}
          >
            Выйти из аккаунта
          </button>
        </div>
      </div>
    </Modal>
  );
}
